import axios from 'axios';
import _, { DebouncedFunc } from 'lodash';
import { encryptPayload } from 'obfuscated/inquiry/lib/utils/crypto';

import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import { UiStepType, VerificationStepType } from 'lib/interfaces/StepsConfig';
import { captureException } from 'lib/reporting';
import SessionClientVerify from 'verify/lib/clients/SessionClientVerify';

const QUEUE_MAX_LIMIT = 5000;
const BATCH_SIZE = 100;
export const THROTTLE_WAIT_TIME = 2000;
const TRACKING_EVENTS_URL = import.meta.env.VITE_TRACKING_EVENTS_URL;

export class TrackingEventsClient {
  private static instance: TrackingEventsClient;

  private queue: TrackingEvent[] = [];

  private throttled: DebouncedFunc<() => Promise<void>> | undefined;

  private constructor() {}

  public static getInstance(): TrackingEventsClient {
    if (!TrackingEventsClient.instance) {
      TrackingEventsClient.instance = new TrackingEventsClient();
    }
    return TrackingEventsClient.instance;
  }

  public static isCloudRunEnabled(featureFlags?: FeatureFlagsMap) {
    return featureFlags?.['inquiry:enable-te-cloud-run'];
  }

  public static track(params: TrackEventParams, featureFlags: FeatureFlagsMap) {
    if (!TrackingEventsClient.isCloudRunEnabled(featureFlags)) {
      return;
    }

    const stepAndPageName = getStepAndPageName(params.pathName, params.stepName);
    const dataAsArray =
      'data' in params ? (Array.isArray(params.data) ? params.data : [params.data]) : [];
    const createdAt = new Date().toISOString();

    let events;
    if (dataAsArray.length === 0) {
      events = [{ type: params.type, created_at: createdAt, data: { ...stepAndPageName } }];
    } else {
      events = dataAsArray.map((data) => ({
        type: params.type,
        created_at: createdAt,
        data: { ...stepAndPageName, ...data },
      }));
    }

    // todo(lauren): fix typing
    this.getInstance().enqueueEvents(events as TrackingEvent[]);
  }

  private updateQueue(events: TrackingEvent[]) {
    if (this.queue.length >= QUEUE_MAX_LIMIT) {
      captureException(
        new Error('TrackingEventsClient: Cannot enqueue anymore TrackingEvents'),
        'info',
      );
      return false;
    }

    this.queue.push(...events);

    return true;
  }

  private enqueueEvents(events: TrackingEvent[]) {
    if (!this.updateQueue(events)) {
      return;
    }

    if (!this.throttled) {
      // Set up a throttler so that we batch all events and only send them every THROTTLE_WAIT_TIME ms
      this.throttled = _.throttle(async () => {
        // If the user is not authenticated, don't attempt to send the events and leave them in the queue
        if (!this.isAuthenticated()) {
          return;
        }

        // NB: The queue must be copied before sending as we might enqueue more events
        // before the network request finishes sending
        const copiedQueue = [...this.queue];
        this.queue = [];
        const failedEvents = await this.sendAllEvents(copiedQueue);

        // requeue the failed events if there are any
        if (failedEvents.length > 0) {
          this.updateQueue(failedEvents);
        }
      }, THROTTLE_WAIT_TIME);
    }
    this.throttled();
  }

  private async sendAllEvents(events: TrackingEvent[]): Promise<TrackingEvent[]> {
    if (events.length <= 0) {
      return [];
    }

    const batchedEvents = _.chunk(events, BATCH_SIZE);
    const results = await Promise.allSettled(
      batchedEvents.map((events) => {
        return this.sendBatch(events);
      }),
    );

    // Maintain an array of events that we should re-enqueue because the promise got rejected
    const failedBatches = results
      .map((result, index) => (result.status === 'rejected' ? batchedEvents[index] : []))
      .filter((batch) => batch.length > 0);

    return _.flatten(failedBatches);
  }

  private async sendBatch(events: TrackingEvent[]): Promise<{ status: 'ok' }> {
    const encrypted = await encryptPayload(JSON.stringify({ events }));

    // Send this to cloud run to be processed
    return axios.post(
      `${TRACKING_EVENTS_URL}/t`,
      { e: encrypted },
      { headers: { Authorization: `Bearer ${SessionClientVerify?.accessToken}` } },
    );
  }

  private isAuthenticated() {
    return SessionClientVerify.accessToken != null;
  }
}

type TrackEventParams = {
  stepName?: string;
  pathName: string;
} & (
  | TrackEventOmittingCommonData<'inquiry.ui.click', ClickTrackingEvent['data']>
  | TrackEventOmittingCommonData<'inquiry.camera.dimensions', CameraDimensionsEvent['data']>
  | TrackEventOmittingCommonData<'inquiry.screen.dimensions', ScreenDimensionsEvent['data']>
  | TrackEventOmittingCommonData<'inquiry.model.detect', ModelDetectEvent['data']>
  | { type: BasicTrackingEventType }
);

type TrackEventOmittingCommonData<T, D> = {
  type: T;
  data: Omit<D, keyof CommonTrackingEventData> | Omit<D, keyof CommonTrackingEventData>[];
};

type TrackingEvent = {
  created_at: string;
} & (ClickTrackingEvent | CameraDimensionsEvent | ModelDetectEvent | BasicTrackingEvent);

interface ClickTrackingEvent {
  type: 'inquiry.ui.click';
  data: TrackingEventData<{
    button_type: number;
    component_name?: string;
    element_name?: string;
    pointer_type?: string | undefined;
    position_x?: number;
    position_y?: number;
    pressure?: number | undefined;
  }>;
}

interface CameraDimensionsEvent {
  type: 'inquiry.camera.dimensions';
  data: TrackingEventData<{
    old_width: number;
    old_height: number;
    new_width: number;
    new_height: number;
  }>;
}

interface ScreenDimensionsEvent {
  type: 'inquiry.screen.dimensions';
  data: TrackingEventData<{
    old_width: number;
    old_height: number;
    new_width: number;
    new_height: number;
  }>;
}

interface ModelDetectEvent {
  type: 'inquiry.model.detect';
  data: TrackingEventData<{
    name: string;
    box: number[];
    score: number;
    frame: number;
  }>;
}

interface BasicTrackingEvent {
  type: BasicTrackingEventType;
  data: TrackingEventData<Record<never, never>>;
}

type TrackingEventData<T> = T & CommonTrackingEventData;

type CommonTrackingEventData = {
  page_name?: string;
  step_name?: string;
};

// These are tracking event types that don't have any extra fields in data
type BasicTrackingEventType = Extract<
  TrackingEventType,
  'inquiry.page.view' | 'inquiry.ui.background' | 'inquiry.ui.foreground'
>;
type TrackingEventType =
  | 'inquiry.ui.click'
  | 'inquiry.page.view'
  | 'inquiry.ui.background'
  | 'inquiry.ui.foreground'
  | 'inquiry.camera.dimensions'
  | 'inquiry.screen.dimensions'
  | 'inquiry.model.detect';

/**
 * Converts step name and router path name into something more intelligible
 * for backend
 */
function getStepAndPageName(
  pathName: string,
  stepName?: string,
): { step_name?: string; page_name: string } {
  const pathParts = pathName.split('/');

  return {
    step_name: stepName,
    // Format the page name into something like `selfie.prompt`
    // The paths come out like `['', 'inquiry', 'government-id', 'select'], so the first 2 items are irrelevant
    page_name: pathParts
      .slice(2)
      .map((name) => _.snakeCase(name))
      .join('.'),
  };
}

// These are steps that are redirected out of /inquiry/ui,
// see front-end/inquiry/views/InquiryUiView/InquiryUiView.tsx
const REDIRECTED_STEPS = [
  UiStepType.Document,
  UiStepType.Integration,
  UiStepType.GovernmentId,
  UiStepType.Selfie,
  UiStepType.VerifyWithPersona,
] as (UiStepType | VerificationStepType)[];

export function isPathBeingRouted(pathName: string, stepType?: UiStepType | VerificationStepType) {
  return (
    pathName === '/verify' ||
    (pathName === '/inquiry/ui' &&
      stepType &&
      REDIRECTED_STEPS.includes(stepType) &&
      stepType !== UiStepType.Ui)
  );
}
